import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarCheck, Plus } from 'lucide-react';
import { toast } from 'sonner';
import {
  AppointmentStatuses,
  formatDate,
  formatDateTime,
  formatTime,
  messages,
  type Appointment,
} from '@nesc/shared';
import { api, errorMessage, type ListResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { useUrlFilters } from '@/lib/hooks';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Field } from '@/components/ui/field';
import { Input, Select, Textarea } from '@/components/ui/input';
import { SkeletonRows } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/common/status-badge';
import { ConfirmDialog, EmptyState } from '@/components/common/feedback';
import { FilterChips, PageHeader } from '@/components/common/page';

/** S52 アポイント */
export const AppointmentsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { filters, setFilter } = useUrlFilters({ status: 'scheduled', ownerId: '', from: '', to: '' });
  const [resultTarget, setResultTarget] = useState<Appointment | null>(null);
  const [result, setResult] = useState({ status: 'held' as const, resultNote: '', nextAction: '', nextActionDueAt: '' });

  const query = {
    status: filters.status || undefined,
    ownerId: filters.ownerId || undefined,
    from: filters.from || undefined,
    to: filters.to || undefined,
    limit: 50,
  };

  const { data, isLoading } = useQuery({
    queryKey: qk.appointments(query),
    queryFn: () => api.get<ListResponse<Appointment>>('/appointments', query),
  });

  const recordResult = useMutation({
    mutationFn: () =>
      api.post(`/appointments/${resultTarget?.id}/result`, {
        status: result.status,
        resultNote: result.resultNote || undefined,
        nextAction: result.nextAction || undefined,
        nextActionDueAt: result.nextActionDueAt ? new Date(result.nextActionDueAt).toISOString() : undefined,
      }),
    onSuccess: () => {
      toast.success('結果を登録しました');
      void queryClient.invalidateQueries({ queryKey: ['appointments'] });
      void queryClient.invalidateQueries({ queryKey: ['activities'] });
      setResultTarget(null);
      setResult({ status: 'held', resultNote: '', nextAction: '', nextActionDueAt: '' });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  // 今日・今週を上部に固定する
  const now = new Date();
  const todayKey = formatDate(now);
  const grouped = (data?.data ?? []).reduce<Record<string, Appointment[]>>((acc, item) => {
    const key = formatDate(item.scheduledAt);
    (acc[key] ??= []).push(item);
    return acc;
  }, {});

  return (
    <>
      <PageHeader
        title={messages.appointment.title}
        actions={
          <Button onClick={() => navigate('/inbox?handlingStatus=open')}>
            <Plus aria-hidden />
            受信から登録する
          </Button>
        }
      />

      <FilterChips className="mb-3">
        <Select
          value={filters.status as string}
          onChange={(e) => setFilter('status', e.target.value)}
          aria-label="状態で絞り込む"
          className="h-8 w-auto shrink-0 text-note lg:h-8"
        >
          <option value="">状態：すべて</option>
          {AppointmentStatuses.list.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </Select>
        <Input
          type="date"
          value={filters.from as string}
          onChange={(e) => setFilter('from', e.target.value)}
          aria-label="開始日"
          className="h-8 w-auto shrink-0 text-note lg:h-8"
        />
      </FilterChips>

      {isLoading ? (
        <SkeletonRows rows={5} />
      ) : !data || data.data.length === 0 ? (
        <EmptyState
          title={messages.empty.appointments.title}
          description={messages.empty.appointments.description}
          action={{ label: messages.empty.appointments.action, onClick: () => navigate('/inbox') }}
          icon={<CalendarCheck className="size-8" />}
        />
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([date, items]) => (
            <section key={date}>
              <h2 className="tabular mb-2 text-h3">
                {date}
                {date === todayKey && (
                  <span className="ml-2 rounded-control bg-primary px-2 text-note text-primary-foreground">
                    {messages.appointment.today}
                  </span>
                )}
              </h2>
              <ul className="space-y-2">
                {items.map((appointment) => (
                  <li key={appointment.id}>
                    <Card>
                      <CardContent className="space-y-2 p-4">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="tabular text-h3">{formatTime(appointment.scheduledAt)}</span>
                          <button
                            type="button"
                            onClick={() => navigate(`/companies/${appointment.companyId}`)}
                            className="min-w-0 flex-1 truncate text-left text-body font-semibold underline-offset-2 hover:underline"
                          >
                            {appointment.companyName}
                          </button>
                          <StatusBadge kind="appointment_status" value={appointment.status} />
                          <StatusBadge kind="appointment_format" value={appointment.format} />
                        </div>
                        <p className="text-note text-foreground-muted">
                          {appointment.durationMinutes} 分
                          {appointment.location && ` ・ ${appointment.location}`}
                          {appointment.owner?.name && ` ・ 担当 ${appointment.owner.name}`}
                        </p>
                        {appointment.purpose && (
                          <p className="text-body text-foreground-muted">{appointment.purpose}</p>
                        )}
                        {appointment.status === 'scheduled' && (
                          <div className="border-t border-border pt-2">
                            <Button variant="outline" size="sm" onClick={() => setResultTarget(appointment)}>
                              {messages.appointment.recordResult}
                            </Button>
                          </div>
                        )}
                        {appointment.nextAction && (
                          <p className="rounded-control bg-muted p-2 text-note">
                            次回: {appointment.nextAction}
                            {appointment.nextActionDueAt && `（${formatDateTime(appointment.nextActionDueAt)}）`}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!resultTarget}
        onOpenChange={(open) => !open && setResultTarget(null)}
        title={messages.appointment.recordResult}
        summary={`${resultTarget?.companyName} のアポイント結果を登録します。`}
        detail={
          <div className="space-y-3">
            <Field label="結果" required>
              {(props) => (
                <Select
                  {...props}
                  value={result.status}
                  onChange={(e) => setResult((p) => ({ ...p, status: e.target.value as never }))}
                >
                  <option value="held">実施</option>
                  <option value="postponed">延期</option>
                  <option value="canceled">キャンセル</option>
                  <option value="no_show">不在</option>
                </Select>
              )}
            </Field>
            <Field label={messages.appointment.resultNote}>
              {(props) => (
                <Textarea
                  {...props}
                  rows={3}
                  value={result.resultNote}
                  onChange={(e) => setResult((p) => ({ ...p, resultNote: e.target.value }))}
                />
              )}
            </Field>
            <Field label={messages.appointment.nextAction} hint="登録すると次回アクション一覧に表示されます">
              {(props) => (
                <Input
                  {...props}
                  value={result.nextAction}
                  onChange={(e) => setResult((p) => ({ ...p, nextAction: e.target.value }))}
                />
              )}
            </Field>
            {result.nextAction && (
              <Field label={messages.appointment.nextActionDue} required>
                {(props) => (
                  <Input
                    {...props}
                    type="datetime-local"
                    value={result.nextActionDueAt}
                    onChange={(e) => setResult((p) => ({ ...p, nextActionDueAt: e.target.value }))}
                  />
                )}
              </Field>
            )}
          </div>
        }
        confirmText={messages.common.save}
        onConfirm={() => recordResult.mutate()}
        isPending={recordResult.isPending}
      />
    </>
  );
};
